// Aging Report — overdue subscription_ledger rows bucketed by days_overdue.
// 4 buckets (0-15 / 16-30 / 31-60 / 60+); per-subscriber roll-up with
// stacked-bar visualisation. Header KPI strip is clickable to filter the
// table by bucket. Each subscriber row has a 🔔 Notify shortcut.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr, today_ymd_ist } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { DateFilterBar } from '../components/DateFilterBar.js';

type BucketKey = '0_15' | '16_30' | '31_60' | '60_plus';
const BUCKET_KEYS: BucketKey[] = ['0_15', '16_30', '31_60', '60_plus'];
const BUCKET_LABEL_TA: Record<BucketKey, string> = {
  '0_15': '0–15 நாள்',
  '16_30': '16–30 நாள்',
  '31_60': '31–60 நாள்',
  '60_plus': '60+ நாள்',
};
const BUCKET_LABEL_EN: Record<BucketKey, string> = {
  '0_15': '0–15 days',
  '16_30': '16–30 days',
  '31_60': '31–60 days',
  '60_plus': '60+ days',
};
const BUCKET_COLOR: Record<BucketKey, string> = {
  '0_15': 'var(--info)',
  '16_30': 'var(--ink-3)',
  '31_60': 'var(--warn)',
  '60_plus': 'var(--bad)',
};

interface AgingSub {
  subscriber_id: string;
  subscriber: { name_ta: string; name_en: string; phone: string };
  chits_count: number;
  chit_names_list: string[];
  oldest_due_days: number;
  total_outstanding_paise: number;
  bucket_outstanding: Record<BucketKey, number>;
}
interface AgingData {
  snapshot_date: string;
  buckets: Record<BucketKey, { count: number; outstanding_paise: number }>;
  subscribers: AgingSub[];
  total_outstanding_paise: number;
}

export function ReportsAgingPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const bucket_label = (k: BucketKey): string => (lang === 'ta' ? BUCKET_LABEL_TA[k] : BUCKET_LABEL_EN[k]);

  const [snapshot_date, set_snapshot_date] = useState(today_ymd_ist());
  const [bucket_filter, set_bucket_filter] = useState<BucketKey | null>(null);

  const q = useQuery({
    queryKey: ['report-aging', snapshot_date],
    queryFn: () => orpc.reports.aging({ snapshot_date }) as Promise<AgingData>,
  });
  const data = q.data;

  const filtered_subs = useMemo(() => {
    if (!data) return [];
    if (!bucket_filter) return data.subscribers;
    return data.subscribers.filter((s) => s.bucket_outstanding[bucket_filter] > 0);
  }, [data, bucket_filter]);

  const export_csv = (): void => {
    if (!data) return;
    const lines = [
      'subscriber_id,name,phone,chits_count,chit_names,oldest_due_days,bucket_0_15,bucket_16_30,bucket_31_60,bucket_60_plus,total_outstanding',
      ...data.subscribers.map((s) =>
        [
          s.subscriber_id,
          (s.subscriber.name_en || s.subscriber.name_ta).replace(/,/g, ' '),
          s.subscriber.phone,
          s.chits_count,
          s.chit_names_list.join('|').replace(/,/g, ' '),
          s.oldest_due_days,
          (s.bucket_outstanding['0_15'] / 100).toFixed(2),
          (s.bucket_outstanding['16_30'] / 100).toFixed(2),
          (s.bucket_outstanding['31_60'] / 100).toFixed(2),
          (s.bucket_outstanding['60_plus'] / 100).toFixed(2),
          (s.total_outstanding_paise / 100).toFixed(2),
        ].join(','),
      ),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aging-${snapshot_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.aging_pdf({ snapshot_date }),
    onSuccess: (data2) => {
      if (data2.preview_html) set_preview(data2.preview_html);
      else window.open(data2.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('வயது அறிக்கை', 'Aging Report')}</div>
          <div className="h-sub">{t('நாள்', 'As of')} {snapshot_date}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateFilterBar mode="single" value={snapshot_date} on_change={set_snapshot_date} />
          <button type="button" className="btn" onClick={export_csv} disabled={!data || data.subscribers.length === 0}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Bucket KPIs — clickable to filter the table */}
      <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {BUCKET_KEYS.map((k) => {
          const active = bucket_filter === k;
          const bk = k === '31_60' ? ' warn' : k === '60_plus' ? ' warn' : '';
          return (
            <button
              key={k}
              type="button"
              className={'kpi' + bk}
              onClick={() => set_bucket_filter(active ? null : k)}
              style={{ border: active ? '2px solid var(--primary)' : '1px solid var(--line)', background: active ? 'var(--primary-soft)' : undefined, textAlign: 'left', cursor: 'pointer' }}
            >
              <div className="kpi-label">{bucket_label(k)}</div>
              <div className="kpi-value">{data ? format_inr(data.buckets[k].outstanding_paise) : '—'}</div>
              <div className="kpi-sub">{data ? `${data.buckets[k].count} ${t('பதிவுகள்', 'items')}` : ''}</div>
            </button>
          );
        })}
      </div>

      {q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(q.error)}</div>}
      {data && filtered_subs.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
          {bucket_filter
            ? t('இந்த பிரிவில் யாரும் இல்லை', 'No subscribers in this bucket')
            : t('நிலுவை இல்லை — 100% வசூல்', 'No overdue accounts — 100% collected')}
        </div>
      )}

      {data && filtered_subs.length > 0 && (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('உறுப்பினர்', 'Member')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('சீட்டுகள்', 'Chits')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, minWidth: 160 }}>{t('பிரிவு பகிர்வு', 'Bucket distribution')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பழமை', 'Oldest')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('மொத்த நிலுவை', 'Total outstanding')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered_subs.map((s, i) => {
                  const total = s.total_outstanding_paise || 1;
                  return (
                    <tr key={s.subscriber_id} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--ink-3)' }}>{i + 1}</td>
                      <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                        <Link to={`/subscribers/${s.subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                          {(lang === 'ta' ? s.subscriber.name_ta : s.subscriber.name_en) || '—'}
                        </Link>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.subscriber.phone}</div>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <div className="mono" style={{ fontWeight: 600 }}>{s.chits_count}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.chit_names_list.slice(0, 2).join(', ')}</div>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--bg-elev)' }}>
                          {BUCKET_KEYS.map((k) => {
                            const pct = (s.bucket_outstanding[k] / total) * 100;
                            if (pct === 0) return null;
                            return <div key={k} style={{ width: `${pct}%`, background: BUCKET_COLOR[k] }} title={`${bucket_label(k)}: ${format_inr(s.bucket_outstanding[k])}`} />;
                          })}
                        </div>
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: s.oldest_due_days > 60 ? 'var(--bad)' : s.oldest_due_days > 30 ? 'var(--warn)' : 'var(--ink)' }} className="mono">
                        {s.oldest_due_days}d
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }} className="mono">{format_inr(s.total_outstanding_paise)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {!bucket_filter && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--bg-elev)' }}>
                    <td colSpan={5} style={{ padding: '8px 10px', fontWeight: 600 }}>{t('மொத்த நிலுவை', 'Total outstanding')}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }} className="mono">{format_inr(data.total_outstanding_paise)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="aging-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
